// Original fiction, authored for this public demo. No social-media posts or real profiles.
// Fixed editorial snapshot, deliberately independent of the visitor's clock or account.
import { narrative, fragments, reflections } from "./narrative.js";
import { emptyState, addDays, weekStart } from "../model.js";
export const PERIOD = Object.freeze({ from: "2026-06-01", to: "2026-08-31" });
const stories = [
  {
    id: "mara",
    name: "Mara",
    pronouns: "she / her",
    birthDate: "1993-02-10",
    theme: "Making room to begin again",
    detail:
      "A bicycle mechanic settling into a new city, one repair and one conversation at a time.",
    place: "Leipzig",
    accent: "lavender",
    compass: {
      values:
        "**Patience. Craft. Belonging.**\n\nI want to be useful without making usefulness my whole identity.",
      calls: "Learn the streets slowly. Find people I can be quiet around.",
      chapter:
        "## A place that feels like mine\n\nBuild a life here before deciding whether I am good at starting over.",
      experiment: "Invite one person for a walk. Leave the route undecided.",
    },
    rows: [
      [0, "A key of my own", "The first cup, sitting on the floor."],
      [3, "First repair", "Being new is a real stage, not a mistake."],
      [7, "Wrong turn", "", true],
      [10, "Dinner for one", "Tomorrow is allowed to be easier."],
      [14, "The wheel finally ran true", "A quarter turn is still movement."],
      [18, "A seat at the table", "", true],
      [23, "Nothing to report", "Ordinary can be enough."],
      [28, "Rain on the workshop roof", "", true],
      [34, "Sunday without a plan", ""],
      [48, "Back here", "Begin where I am."],
      [54, "Saying no kindly", "Rest doesn't need an impressive reason."],
      [60, "A familiar face", "", true],
      [
        68,
        "The walk happened",
        "A friendship can start without an occasion.",
        true,
      ],
      [76, "Still learning", ""],
      [84, "Things that stayed", "Not settled forever. Settled a little."],
      [91, "A place to return to", "I can see the change from here."],
    ],
  },
  {
    id: "eli",
    name: "Eli",
    pronouns: "he / him",
    birthDate: "1985-10-22",
    theme: "A life beyond the timetable",
    detail:
      "A teacher and dad finding small pockets of music, friendship and time that is his own.",
    place: "Bristol",
    accent: "green",
    compass: {
      values:
        "**Presence, play, fairness.**\n\nBeing available doesn't mean never needing time to myself.",
      calls:
        "Pick up the guitar again. Spend time with friends without organising a grand event.",
      chapter:
        "## Enough space for all of us\n\nLet family life include my interests, rather than waiting for a perfect quiet season.",
      experiment:
        "Play guitar for ten minutes on two evenings. No recording, no goal.",
    },
    rows: [
      [0, "The slow way to school", "Five snails. A whole conversation."],
      [3, "Found the guitar", "Make starting smaller."],
      [7, "A difficult lesson", "", true],
      [10, "Pasta again", ""],
      [14, "Ten minutes", "Someone enjoyed it before I improved."],
      [18, "An old friend", "", true],
      [23, "The sports day umbrella", "Look up."],
      [28, "Quiet classroom", "", true],
      [34, "A proper rest", ""],
      [48, "No catching up", "Here is today."],
      [54, "A small arrangement", "Time can be shared without disappearing."],
      [60, "The library trip", "", true],
      [68, "Music in the kitchen", "A normal place for music."],
      [76, "Plans fell through", ""],
      [
        84,
        "An afternoon with friends",
        "Make the invitation, not the perfect room.",
        true,
      ],
      [91, "Before term starts", "Keep it within reach."],
    ],
  },
  {
    id: "ines",
    name: "Ines",
    pronouns: "she / her",
    birthDate: "1964-05-18",
    theme: "Curiosity without a deadline",
    detail:
      "An architect easing into part-time work and discovering how it feels to be a beginner at drawing.",
    place: "Porto",
    accent: "sand",
    compass: {
      values:
        "**Curiosity. Independence. Generosity.**\n\nI can make something without turning it into a project.",
      calls:
        "Draw outside. Learn the names of the plants on my street. See friends on an ordinary weekday.",
      chapter:
        "## A different pace\n\nWork still matters. I am making room for other things to matter too.",
      experiment:
        "Take a sketchbook on one walk. Stop before the drawing feels finished.",
    },
    rows: [
      [0, "The first free Monday", "A day doesn't have to be earned twice."],
      [3, "A blank sketchbook", "Start with what's in front of me."],
      [7, "Lunch with a friend", "", true],
      [10, "A badly drawn chair", ""],
      [14, "Under the trees", "The looking was the point.", true],
      [18, "Back at the studio", ""],
      [23, "The empty afternoon", "Freedom takes practice too."],
      [28, "A new word", "", true],
      [34, "No drawing today", ""],
      [48, "Returning to the page", "Return without an apology."],
      [54, "A younger colleague", "Experience can mean better questions."],
      [60, "Market colours", "", true],
      [68, "Drawing with company", "There was a spare chair.", true],
      [76, "Letting the plan change", ""],
      [84, "An unfinished page", "I get to choose where to stop."],
      [
        91,
        "A shelf for the sketchbooks",
        "A beginning can become a habit quietly.",
      ],
    ],
  },
  {
    id: "noah",
    name: "Noah",
    pronouns: "he / him",
    birthDate: "2002-11-03",
    theme: "Finding a rhythm of my own",
    detail:
      "An apprentice cook balancing late shifts, running and friendships that need a little tending.",
    place: "Melbourne",
    accent: "blue",
    compass: {
      values:
        "**Friendship. Learning. Steadiness.**\n\nI want ambition to leave room for being a person outside work.",
      calls:
        "Get outdoors before a late shift. Keep in touch even when our schedules don't match.",
      chapter:
        "## Learning the pace\n\nBuild routines that work with the hours I actually have.",
      experiment:
        "Send a voice note to a friend on my walk home. Ask one real question.",
    },
    rows: [
      [0, "First full service", "Someone made sure I ate."],
      [3, "Morning without an alarm", "My hours are still real hours."],
      [7, "A short run", "", true],
      [10, "Too much salt", ""],
      [
        14,
        "A message back",
        "Connection doesn't have to happen at the same hour.",
      ],
      [18, "Lunch with my sister", "", true],
      [23, "An ordinary good shift", "Steady is something."],
      [28, "Cold air", "", true],
      [34, "A missed birthday", ""],
      [48, "Hello again", "No backlog. One page."],
      [54, "Asked the question", "Ask sooner."],
      [60, "Breakfast finally", "", true],
      [68, "The long loop", "", true],
      [76, "A tired evening", ""],
      [84, "A new recipe at home", "A messy table can be a good evening."],
      [91, "A rhythm, roughly", "Not finished. More familiar."],
    ],
  },
];
function freeze(value) {
  if (value && typeof value === "object") {
    Object.values(value).forEach(freeze);
    Object.freeze(value);
  }
  return value;
}
export const personas = freeze(
  stories.map(({ rows, ...person }) => {
    const journal = emptyState();
    journal.profile = {
      name: person.name,
      birthDate: person.birthDate,
      horizon: 81,
    };
    journal.compass = person.compass;
    journal.entries = rows.map(([offset, title, memory, place], i) => ({
      id: `${person.id}-${i}`,
      date: addDays(PERIOD.from, offset),
      title,
      text: narrative[person.id][i],
      memory: i % 4 === 0 ? memory : "",
      createdAt: `${addDays(PERIOD.from, offset)}T22:00:00Z`,
      ...(place ? { location: { label: person.place, source: "manual" } } : {}),
    }));
    // A day with several separate notes exercises pagination without repetitive filler.
    fragments[person.id].forEach((text, i) =>
      journal.entries.push({
        id: `${person.id}-small-${i}`,
        date: "2026-08-24",
        title: "",
        text,
        memory: "",
        createdAt: `2026-08-24T${String([8, 9, 17, 11, 12, 23][i]).padStart(2, "0")}:00:00Z`,
      }),
    );
    for (let i = 0; i <= 91; i++) {
      // Two quiet weeks plus irregular gaps; no invented perfect streak.
      if (i >= 35 && i < 48) continue;
      if (i % 7 !== 5 && i % 11 !== 4)
        journal.lived[addDays(PERIOD.from, i)] = true;
    }
    // Writing and marking lived are independent in Weekkeep.
    journal.lived["2026-06-11"] = false;
    for (const date of ["2026-06-22", "2026-07-27", "2026-08-24"]) {
      journal.closures[weekStart(person.birthDate, date)] = {
        memory:
          reflections[person.id][
            ["2026-06-22", "2026-07-27", "2026-08-24"].indexOf(date)
          ],
        experiment: person.compass.experiment,
      };
    }
    return { ...person, journal };
  }),
);
export const findPersona = (id) => personas.find((p) => p.id === id);
